import type { CheckoutInterval } from '@kimuno/api-sdk/lib/types/requests/Billing';
import { Alert } from '@mui/material';
import { PaymentElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout';
import React, { useEffect, useRef, useState } from 'react';

import billingService from '../../../../services/billing.service';
import { checkoutSignature } from '../checkout.helpers';
import type { IntervalSyncState, PromoSyncState, StripeCheckout } from '../checkout.types';
import { PaymentFormSkeleton } from './CheckoutSkeletons';

/** The promotion code currently applied to a session (from `discountAmounts`). */
const currentPromoOf = (checkout: StripeCheckout): string =>
	checkout.discountAmounts?.find((d) => d.promotionCode)?.promotionCode ?? '';

/**
 * The session's current billing interval in the checkout request vocabulary
 * (`monthly`/`yearly`), mapped from Stripe's recurring `interval`
 * (`month`/`year`). Null until the recurring block is known.
 */
const currentIntervalOf = (checkout: StripeCheckout): CheckoutInterval | null => {
	switch (checkout.recurring?.interval) {
		case 'month':
			return 'monthly';
		case 'year':
			return 'yearly';
		default:
			return null;
	}
};

// Safety net: if the element never signals ready/error (e.g. a stuck load), open
// the in-place-update gate anyway after this long so a link-borne code / a
// seeded interval still applies.
const PROMO_GATE_FALLBACK_MS = 8000;

/**
 * The single payment field (SM-375). Stays mounted across interval and promo
 * changes — both are applied in place on the live session (interval:
 * `runServerUpdate` line-item swap; promo: `applyPromotionCode`), so any card
 * details the user already typed survive. Reports its session up to the page so
 * prices, terms, captcha and submit can live at page level.
 */
const CheckoutPaymentElement: React.FC<{
	email: string;
	canUpdateEmail: boolean;
	/** Desired promo code for this session; empty string = none applied. */
	promoCode: string;
	/** Desired billing interval; swapped in place when it differs from the session's. */
	interval: CheckoutInterval;
	onReport: (checkout: StripeCheckout | null) => void;
	/** Reports the in-place promo apply/remove lifecycle up to the page. */
	onPromoSync: (state: PromoSyncState) => void;
	/** Reports the in-place interval-swap lifecycle up to the page. */
	onIntervalSync: (state: IntervalSyncState) => void;
}> = ({ email, canUpdateEmail, promoCode, interval, onReport, onPromoSync, onIntervalSync }) => {
	const checkoutState = useCheckoutElements();
	const checkout = checkoutState.type === 'success' ? checkoutState.checkout : null;

	// `useCheckoutElements()` returns a fresh object each render. Keep the live
	// one in a ref and drive effects off primitive fields only, so they don't
	// re-run every render (which would thrash state updates and freeze the page).
	const checkoutRef = useRef<StripeCheckout | null>(checkout);
	checkoutRef.current = checkout;

	const checkoutId = checkout?.id ?? null;
	const checkoutEmail = checkout?.email ?? null;
	// Change-signature over id + confirmability + all rendered price fields +
	// recurring interval. A promo or interval swap applied in place keeps the same
	// id, so this is what makes the new prices/interval propagate up (see
	// `checkoutSignature`).
	const signature = checkout ? checkoutSignature(checkout) : null;
	// Session's current billing interval as a primitive — the interval-swap effect
	// keys on this (not the full `signature`) so it reruns when the interval
	// actually changes, not on every unrelated price-field update (e.g. a promo).
	const sessionInterval = checkout ? currentIntervalOf(checkout) : null;

	// Last promo / interval target we attempted on this session — dedupes
	// re-renders and stops a target that never lands (e.g. an invalid promo, which
	// never enters `discountAmounts`) from being re-applied on every render.
	const lastPromoAttemptRef = useRef<string | null>(null);
	const lastIntervalAttemptRef = useRef<CheckoutInterval | null>(null);

	// Applying a promo / swapping the interval in the first moments after the
	// session reports `success` makes Stripe's mutation hang ~16s (the
	// session/customer is still settling server-side); the same call is <1s once
	// the payment element is ready. So gate the first in-place update on element
	// readiness — set true on the element's `onReady`, or on load error / a
	// fallback timer so a link-borne code / seeded interval still applies if
	// `onReady` never fires.
	const [elementReady, setElementReady] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		onReport(checkoutRef.current);
	}, [signature, onReport]);

	useEffect(() => {
		if (!checkoutId || elementReady) return;
		const timer = window.setTimeout(() => setElementReady(true), PROMO_GATE_FALLBACK_MS);
		return () => window.clearTimeout(timer);
	}, [checkoutId, elementReady]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const current = checkoutRef.current;
		if (!current || !canUpdateEmail || !email) return;
		if (checkoutEmail === email) return;
		let cancelled = false;
		(async () => {
			const result = await current.updateEmail(email);
			if (cancelled) return;
			if (result.type === 'error') {
				console.warn('Stripe rejected email at updateEmail():', result.error);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [checkoutId, checkoutEmail, canUpdateEmail, email]);

	// Apply / remove the promo code in place on the live session. Updating the
	// session this way (instead of recreating it) keeps the <PaymentElement>
	// mounted, so any card details the user already typed survive a promo change.
	const desiredPromo = promoCode.trim();
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const current = checkoutRef.current;
		if (!current) return;

		const inDesiredState = desiredPromo.toLowerCase() === currentPromoOf(current).toLowerCase();

		// Wait until the session has settled (element ready) before the first apply —
		// an apply fired too early hangs ~16s. Later toggles pass this immediately.
		// While gated with a change still pending, surface the applying state now so
		// the price shows a loading treatment instead of a stale (pre-promo) total.
		if (!elementReady) {
			onPromoSync({ applying: !inDesiredState });
			return;
		}

		// Already in the desired state — nothing to do; keep the attempt ref in sync.
		if (inDesiredState) {
			lastPromoAttemptRef.current = desiredPromo;
			return;
		}
		if (lastPromoAttemptRef.current === desiredPromo) return;
		lastPromoAttemptRef.current = desiredPromo;

		let cancelled = false;
		onPromoSync({ applying: true });
		(async () => {
			try {
				const result = desiredPromo
					? await current.applyPromotionCode(desiredPromo)
					: await current.removePromotionCode();
				if (cancelled) return;
				if (result.type === 'error') {
					const invalid = 'code' in result.error && result.error.code === 'invalidCode';
					onPromoSync({
						applying: false,
						error: invalid ? 'invalid' : 'network',
					});
					return;
				}
				onPromoSync({ applying: false });
			} catch {
				// Reached e.g. when the session was created without
				// `allow_promotion_codes` — Stripe throws an IntegrationError here.
				if (cancelled) return;
				onPromoSync({ applying: false, error: 'network' });
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [desiredPromo, checkoutId, onPromoSync, elementReady]);

	// Swap the billing interval in place on the live session (SM-375). A line-item
	// swap has no client-only method (unlike `applyPromotionCode`), so it is routed
	// server-side via `runServerUpdate`: kimuno re-resolves the plan's line items +
	// tax for the new interval and updates the session, and the mounted element
	// re-fetches + refreshes totals in place — no re-init, card input survives.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const current = checkoutRef.current;
		if (!current) return;

		// Unknown until the recurring block lands — wait for the next render.
		if (sessionInterval === null) return;

		const inDesiredState = interval === sessionInterval;

		// Gate the first swap on element readiness (same ~16s early-hang as promo).
		// While gated with a change still pending, surface the updating state so the
		// price shows a loading treatment instead of the stale interval's total.
		if (!elementReady) {
			onIntervalSync({ updating: !inDesiredState });
			return;
		}

		if (inDesiredState) {
			lastIntervalAttemptRef.current = interval;
			return;
		}
		if (lastIntervalAttemptRef.current === interval) return;
		lastIntervalAttemptRef.current = interval;

		let cancelled = false;
		onIntervalSync({ updating: true });
		(async () => {
			try {
				const result = await current.runServerUpdate(async () => {
					await billingService.updateCheckoutSession(current.id, { interval });
				});
				if (cancelled) return;
				if (result.type === 'error') {
					onIntervalSync({ updating: false, error: 'network' });
					return;
				}
				onIntervalSync({ updating: false });
			} catch {
				if (cancelled) return;
				onIntervalSync({ updating: false, error: 'network' });
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [interval, checkoutId, sessionInterval, onIntervalSync, elementReady]);

	if (checkoutState.type === 'loading') {
		return <PaymentFormSkeleton />;
	}

	if (checkoutState.type === 'error') {
		return (
			<Alert severity="error" data-testid="stripe-checkout-elements-error">
				{checkoutState.error.message}
			</Alert>
		);
	}

	return (
		<PaymentElement
			onReady={() => setElementReady(true)}
			onLoadError={() => setElementReady(true)}
			options={{
				fields: {
					billingDetails: {
						address: { country: 'never' },
					},
				},
			}}
		/>
	);
};

export default CheckoutPaymentElement;
